import {inject, Injectable, resource, signal} from '@angular/core';
import {Observable} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {AvoirExterneDTO} from '../../dtos/facturation/fournisseurs/avoirs/avoir-externe-dto';
import {AvoirExterneFilters} from '../../dtos/facturation/fournisseurs/avoirs/avoir-externe-filter-dto';
import {GraphQLService} from '../technique/graphql.service';
import {HttpService} from '../technique/http.service';
import {ResponseWrapper} from '../../suppliers/wrappers/response-wrapper';
import {CegidFiltersDTO} from '../../dtos/facturation/fournisseurs/cegid/cegid-filters-dto';
import {CegidExportResultDTO} from '../../dtos/facturation/fournisseurs/cegid/cegid-export-result-dto';

@Injectable({providedIn: 'root'})
export class AvoirExterneService {

  private readonly graphQlSvc = inject(GraphQLService);
  private readonly httpSvc = inject(HttpService);

  validerEcart = (id: number): Observable<ResponseWrapper<boolean>> =>
    this.httpSvc.post(`dolrest/avoir-externe/rapprochement/${id}/valider-ecart`, {});

  valider = (id: number): Observable<ResponseWrapper<boolean>> =>
    this.httpSvc.post(`dolrest/avoir-externe/${id}/valider`, {});

  abandonner = (id: number): Observable<ResponseWrapper<boolean>> =>
    this.httpSvc.post(`dolrest/avoir-externe/${id}/abandonner`, {});

  refuser = (id: number): Observable<ResponseWrapper<boolean>> =>
    this.httpSvc.post(`dolrest/avoir-externe/${id}/refuser`, {});

  rapprocherManuellement = (avoirExterneId: number, avoirFournisseurId: number): Observable<ResponseWrapper<boolean>> =>
    this.httpSvc.post(`dolrest/avoir-externe/${avoirExterneId}/rapprocher-manuellement?avoirFournisseurId=${avoirFournisseurId}`, {});

  findById = (id: number): Observable<AvoirExterneDTO> =>
    this.graphQlSvc.sendQuery(`
      {
        allAvoirExterne(filters: { idList: [${id}] }) {
          id,
          source,
          externalReference,
          numero,
          dateFacture,
          dateSaisieComptable,
          fournisseurCode,
          siteExterneId,
          siteExterneLibelle,
          montantHT,
          montantTTC,
          tva1Montant, tva1Taux,
          tva2Montant, tva2Taux,
          tva3Montant, tva3Taux,
          tva4Montant, tva4Taux,
          tva5Montant, tva5Taux,
          documentNom,
          url,
          dateCreationSource,
          dateRapprochement,
          statut,
          rapprochementStatut,
          factureBloquante,
          rapprochementList {
            id,
            avoirExterneId,
            numeroBonLivraison,
            bonReceptionId,
            rapproche,
            avoirFournisseurId,
            avoirNumero,
            montantHT,
            montantTTC,
            tva1Montant, tva1Taux,
            tva2Montant, tva2Taux,
            tva3Montant, tva3Taux,
            tva4Montant, tva4Taux,
            tva5Montant, tva5Taux
          }
        }
      }
    `).pipe(
      take(1),
      map((response: any) => (response?.allAvoirExterne?.[0] ?? null) as AvoirExterneDTO)
    );

  exportCegidFactureAvoir = (filters: CegidFiltersDTO = {}): Observable<CegidExportResultDTO> =>
    this.httpSvc.post('dolrest/api/export/cegid/facture-avoir', filters).pipe(
      map((response: ResponseWrapper<CegidExportResultDTO>) => response.one)
    );

  readonly filtersSignal = signal<AvoirExterneFilters>({ page: 0, limit: 20 });

  readonly avoirExterneResource = resource<AvoirExterneDTO[], AvoirExterneFilters>({
    params: () => this.filtersSignal(),
    loader: async ({ params: filters }) => {
      const parts: string[] = [];
      if (filters.numero) parts.push(`numero: "${filters.numero}"`);
      if (filters.siteExterneLibelle) parts.push(`siteExterneLibelle: "${filters.siteExterneLibelle}"`);
      if (filters.avoirNumero) parts.push(`avoirNumero: "${filters.avoirNumero}"`);
      if (filters.statut) parts.push(`statut: "${filters.statut}"`);
      if (filters.rapprochementStatut) parts.push(`rapprochementStatut: "${filters.rapprochementStatut}"`);
      if (filters.dateFacture) parts.push(`dateFacture: "${filters.dateFacture}"`);
      if (filters.dateRapprochement) parts.push(`dateRapprochement: "${filters.dateRapprochement}"`);
      if (filters.factureBloquante != null) parts.push(`factureBloquante: ${filters.factureBloquante}`);
      if (filters.dateReceptionDebut) parts.push(`dateReceptionDebut: "${filters.dateReceptionDebut}"`);
      if (filters.dateReceptionFin) parts.push(`dateReceptionFin: "${filters.dateReceptionFin}"`);
      if (filters.page != null) parts.push(`page: ${filters.page}`);
      if (filters.limit != null) parts.push(`limit: ${filters.limit}`);
      const filtersGql = parts.length ? `(filters: { ${parts.join(', ')} })` : '';

      const response: any = await this.graphQlSvc.sendQuery(`
        {
          allAvoirExterne${filtersGql} {
            id,
            numero,
            statut,
            rapprochementStatut,
            dateFacture,
            dateRapprochement,
            fournisseurCode,
            siteExterneLibelle,
            factureBloquante,
            montantHT,
            montantTTC,
            rapprochementList {
              avoirNumero
            }
          }
        }
      `).pipe(take(1)).toPromise();
      return (response?.allAvoirExterne ?? []) as AvoirExterneDTO[];
    }
  });
}
